<template>
  <div>
    <vx-card>
      <p class="mb-5">
        Information in this section (Introduction) does not need to be learned or memorised and you
        will not be quizzed on it.
      </p>
      <p>
        This information is provided for you to read through and gain an understanding of this
        particular career path before starting your Virtual Job Experience.
      </p>
    </vx-card>
    <vx-card class="mt-base mb-10">
      <h4 class="mb-5">
        Let’s start your virtual job experience as a Fitness Instructor/Personal Trainer.
      </h4>

      <p class="mb-5">
        Being a Fitness Instructor/Personal Trainer can be a hugely rewarding yet challenging
        career. While both jobs draw on interchangeable skill sets like excellent communication and
        fitness levels, there are some noticeable differences between the two roles.
      </p>
      <p class="mb-5">So, what’s the difference?</p>
      <p class="mb-5">
        Personal Trainers (or PTs) usually start out as Fitness Instructors first before moving on
        to personal training as their experience grows and they gain more qualifications.
      </p>
      <p class="mb-5">
        PTs spend most of their time working one-on-one with people to help them meet their fitness
        goals, motivate them and keep them on track. They might also offer emotional support and
        lifestyle advice to help clients smash their goals!
      </p>

      <div class="vx-row mt-5 flex justify-center">
        <div class="vx-col xl:w-3/4 lg:w-2/3 w-full my-5">
          <p class="mb-5">
            On the other hand, Fitness Instructors are more likely to lead group exercise classes.
          </p>
          <p class="mb-5">
            They might have duties making sure gym clients use the equipment safely, as well as
            taking on responsibilities like cleaning (someone’s gotta do it) or administration work.
          </p>
          <p class="mb-5">
            Both group instructors and PTs are responsible for planning and choreographing their own
            classes, not to mention keeping it interesting! This can look like:
          </p>
          <ul class="default-list">
            <li>Creating fun and effective routines</li>
            <li>Organising exercise programmes</li>
            <li>Choosing appropriate music</li>
            <li>Performing assessments</li>
            <li>Scheduling clients</li>
            <li>Administrative duties</li>
          </ul>
        </div>
        <div class="vx-col xl:w-1/4 lg:w-1/3 w-full my-5">
          <img
            src="@/assets/images/experiences/fitness/ft_1.jpg"
            alt=""
            class="mx-auto img-responsive"
          />
        </div>
      </div>

      <p class="mb-5">
        On the plus side, if they work for fitness companies, many of these programmes have already
        been created for them. It is then up to the trainers to deliver the content of those
        programmes the best way they can to keep clients engaged and pumped to keep going.
      </p>
      <p class="mb-5 text-bold">Choose To Work For Yourself Or A Company</p>
      <p class="mb-5">
        Although lots of PT’s work for gyms, many are self-employed. This means they’re responsible
        for designing routines and programmes by themselves. On top of that, they will often have to
        do their own marketing to get as many clients as they can.
      </p>
      <p class="mb-5">
        While this might seem intimidating, there’s a lot of freedom in working for yourself - you
        have a say in how your day might look and what kinds of activities you want to do with your
        clients!
      </p>
      <p class="mb-5">
        Usually, the first step they take is evaluating a potential client’s current fitness level
        along with their personal goals, they then use this information to design personalised
        training programmes just for them.
      </p>
      <p class="mb-5 text-bold">Work Outside, Inside Or Even An Office</p>
      <p class="mb-5">
        Both Fitness Instructors and PTs can work in a variety of environments. As well as gyms and
        health clubs, they can work from recreation centres, country clubs, universities, hospitals,
        resorts, studios, public parks, and even the client’s home. Some even work in offices
        developing fitness programmes for employees.
      </p>
      <div class="vx-row mt-5 flex justify-center">
        <div class="vx-col xl:w-2/3 lg:w-2/3 w-full my-5">
          <p class="mb-5">
            To be successful in this industry you need to be sociable and good with people. As this
            career is heavily focused on building strong relationships you'll need excellent
            communication skills.
          </p>
          <p class="mb-5">
            You’ll also have to ‘walk the walk’ - most clients will be put off by a trainer who is
            out of shape - so be sure to get yourself into good physical shape and stay there!
          </p>
        </div>
        <div class="vx-col xl:w-1/3 lg:w-1/3 w-full my-5">
          <img
            src="@/assets/images/experiences/fitness/ft_2.jpg"
            alt=""
            class="mx-auto img-responsive"
          />
        </div>
      </div>
    </vx-card>

    <vx-card title="Education">
      <p class="mb-5">
        You usually need a certificate III or IV in fitness to work as a Fitness Instructor.
      </p>
      <p class="mb-5">
        Before starting a course, check it will provide you with the skills and qualifications you
        need. Visit:
      </p>
      <p class="mb-5">
        <a href="https://www.myskills.gov.au/" target="_blank">My Skills</a> to compare Vocational
        Education and Training (VET) courses, providers and student outcomes.
      </p>
      <p class="mb-10">
        <a href="https://www.aapathways.com.au/career-research/job-pathways" target="_blank"
          >AAPathways</a
        >
        website to explore Sport, Fitness and Recreation VET training pathways.
      </p>
      <p class="mb-5 text-bold text-underline">Useful Resources:</p>

      <p class="mb-5"><a href="https://fitness.org.au/" target="_blank">Fitness Australia</a></p>
      <p class="mb-5">
        <a href="https://www.fitnessnetwork.com.au/" target="_blank">Australian Fitness Network</a>
      </p>
    </vx-card>

    <vx-card title="Quick Details">
      <div class="vx-row mt-5 flex justify-center">
        <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
          <ul class="default-list">
            <li><strong>Size:</strong> This is a large occupation.</li>
            <li>
              <strong>Earnings:</strong> Full-time workers on an adult wage earn around $1,126 per
              week (below the average of $1,460). Earnings tend to be lower when starting out and
              higher as experience grows.
            </li>
            <li>
              <strong>Full-time:</strong> Less than half work full-time (29%, less than the average
              of 66%), showing there are many opportunities to work part-time.
            </li>
            <li>
              <strong>Hours:</strong> Full-time workers spend around 45 hours per week at work
              (compared to the average of 44 hours).
            </li>
            <li>
              <strong>Age:</strong> The average age is 35 years (compared to the average of 40
              years).
            </li>
            <li>
              <strong>Gender:</strong> 60% of workers are female (compared to the average of 48%).
            </li>
          </ul>

          <h4 class="mb-5">Tasks</h4>
          <ul class="default-list">
            <li>
              Consulting with various Health Professionals to develop and design fitness programs
            </li>
            <li>
              Designing individual fitness programs based on an assessment of the client's age,
              level of fitness, goals and abilities
            </li>
            <li>
              Delivering group exercise classes and personal tuition in a variety of fitness
              activities in a safe and creative manner
            </li>
            <li>Demonstrating and teaching body movements and skills used in fitness routines</li>
            <li>
              Setting up and monitoring fitness equipment and ensuring that equipment is safe, clean
              and in working condition
            </li>
            <li>Teaching and advising on the use of fitness equipment</li>
            <li>
              Ensuring clients are aware of and adhere to safety and injury prevention procedures
            </li>
            <li>Reporting accidents and preparing accident reports</li>
            <li>
              Maintaining a working knowledge of current health and safety standards and ensuring
              working practices and procedures conform to current legislation
            </li>
            <li>Maintaining current first aid certificates</li>
          </ul>
        </div>
        <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
          <img
            src="@/assets/images/experiences/fitness/fi_intro.png"
            alt="All fitness instructors job outlook: $1,126 weekly pay, very strong future growth, 37,800 workers, lower skill level rating, 29% full-time, 45 hours average full-time week, average age 35, 60% female."
            class="mx-auto img-responsive"
          />
        </div>
      </div>
      <vs-alert color="dark" title="Caution" active="true">
        The Australian jobs market is changing in response to the COVID-19 pandemic. These estimates
        do not take account of the impact of COVID-19. They may not reflect the current jobs market
        and should be used and interpreted with extreme caution.
      </vs-alert>
    </vx-card>
  </div>
</template>

<script>
export default {
  data() {
    return {
      cover_img: require('@/assets/images/other/cover.jpg'),
      popupActive: false,
      videoTrigger1: require('@/assets/images/experiences/psychology/becoming_a_registered_psychologist_trigger.jpg'),
      videos: {
        1:
          $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
          '/content/psychology/become_a_psychologist.mp4',
      },
      activeTab: 0,
    };
  },
};
</script>
